import React, { useMemo } from 'react';
import { Marker, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import type { Plant } from '../../types';
import { STATUS_COLORS, TYPE_EMOJI, pixelToLatLng } from '../../lib/utils';

interface PlantMarkerProps {
  plant: Plant;
  mapHeight: number;
  selected: boolean;
  onClick: () => void;
  onDragEnd: (lat: number, lng: number) => void;
}

function createIcon(plant: Plant, selected: boolean) {
  const color = STATUS_COLORS[plant.status];
  const emoji = TYPE_EMOJI[plant.plant_type];
  const base = plant.mature_spread ? Math.max(28, Math.min(56, plant.mature_spread * 32)) : 32;
  const size = selected ? base * 1.2 : base;
  const border = selected ? '3px solid #166534' : '2.5px solid white';
  const shadow = selected
    ? '0 0 0 2px #16a34a, 0 4px 12px rgba(0,0,0,0.35)'
    : '0 2px 6px rgba(0,0,0,0.3)';

  return L.divIcon({
    html: `<div class="plant-marker-icon" style="
      width:${size}px;height:${size}px;
      background:${color};
      border:${border};
      border-radius:50%;
      box-shadow:${shadow};
      display:flex;align-items:center;justify-content:center;
      cursor:pointer;
      font-size:${Math.max(10, size * 0.42)}px;
      line-height:1;
      transition:all 0.15s ease;
    ">${emoji}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    className: '',
  });
}

export function PlantMarker({ plant, mapHeight, selected, onClick, onDragEnd }: PlantMarkerProps) {
  if (plant.x == null || plant.y == null) return null;

  const position = pixelToLatLng(plant.x, plant.y, mapHeight);
  // Memoize icon to avoid recreating L.divIcon (and triggering Leaflet DOM updates) on every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const icon = useMemo(() => createIcon(plant, selected), [plant.id, plant.status, plant.plant_type, plant.mature_spread, selected]);

  return (
    <Marker
      position={position}
      icon={icon}
      draggable
      eventHandlers={{
        click: onClick,
        dragend: (e) => {
          const ll = (e.target as L.Marker).getLatLng();
          onDragEnd(ll.lat, ll.lng);
        },
      }}
    >
      <Tooltip
        direction="top"
        offset={[0, -8]}
        className="!bg-white !border-gray-200 !text-gray-800 !text-xs !font-medium !shadow-md"
      >
        <span className="font-semibold">{plant.common_name}</span>
        {plant.cultivar && <span className="text-gray-500"> '{plant.cultivar}'</span>}
      </Tooltip>
    </Marker>
  );
}
